import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Skeleton,
  Grid,
  Button,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { monitoramentoService } from '../../services/api';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

interface TopCidRow {
  cid: string;
  nome_cid: string;
  total_registros: number;
}

interface OrigemRow {
  tabela_origem: string;
  total_registros: number;
}

interface EvolucaoRow {
  mes: string;
  [cid: string]: string | number;
}

interface CidData {
  topCids: TopCidRow[];
  porOrigem: OrigemRow[];
  evolucao: EvolucaoRow[];
}

const CID_COLORS = [
  '#1565c0', '#2e7d32', '#e65100', '#6a1b9a', '#c62828',
  '#00838f', '#4527a0', '#ad1457', '#ef6c00', '#1b5e20',
  '#283593', '#bf360c', '#00695c', '#5d4037', '#37474f',
];

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 350 }) => (
  <Box sx={{ py: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <CircularProgress size={18} thickness={5} sx={{ color: 'text.disabled' }} />
      <Typography variant="body2" color="text.secondary">Carregando dados...</Typography>
    </Box>
    <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 1 }} />
  </Box>
);

const MonitoramentoCid: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<CidData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultFim = new Date();
  const defaultInicio = new Date();
  defaultInicio.setMonth(defaultInicio.getMonth() - 6);

  // Pending filter values (bound to DateRangeSelector UI)
  const [filtroInicio, setFiltroInicio] = useState(toISO(defaultInicio));
  const [filtroFim, setFiltroFim] = useState(toISO(defaultFim));

  // Applied filter values (drive the fetch useEffect)
  const [dataInicio, setDataInicio] = useState(toISO(defaultInicio));
  const [dataFim, setDataFim] = useState(toISO(defaultFim));

  const handleDateChange = useCallback((di: string, df: string) => {
    setFiltroInicio(di);
    setFiltroFim(df);
  }, []);

  const handleBuscar = useCallback(() => {
    setDataInicio(filtroInicio);
    setDataFim(filtroFim);
  }, [filtroInicio, filtroFim]);

  useEffect(() => {
    if (!municipioAtivo) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getCidDiagnosticos(municipioAtivo.id, dataInicio, dataFim);
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de CID/Diagnosticos.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [municipioAtivo, dataInicio, dataFim]);

  // Extract unique CID keys from evolucao data (excluding 'mes')
  const cidKeys = useMemo(() => {
    if (!data?.evolucao?.length) return [];
    const keys = new Set<string>();
    data.evolucao.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key !== 'mes') keys.add(key);
      });
    });
    return Array.from(keys);
  }, [data?.evolucao]);

  // Prepare topCids with truncated label
  const topCidsFormatted = useMemo(() => {
    return (data?.topCids || []).map((row) => {
      const label = `${row.cid} - ${row.nome_cid}`;
      return {
        ...row,
        label_truncated: label.length > 35 ? label.substring(0, 35) + '...' : label,
        label_full: label,
      };
    });
  }, [data?.topCids]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="h5" color="text.secondary">Nenhum municipio selecionado</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Selecione um municipio para visualizar os diagnosticos CID.
        </Typography>
      </Box>
    );
  }

  const porOrigem = data?.porOrigem || [];
  const evolucao = data?.evolucao || [];
  const hasData = !loading && data;
  const isEmpty = hasData && topCidsFormatted.length === 0 && porOrigem.length === 0 && evolucao.length === 0;

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
        CID / Diagnosticos
      </Typography>

      {/* Date Selector + Buscar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <DateRangeSelector
          dataInicio={filtroInicio}
          dataFim={filtroFim}
          onChange={handleDateChange}
        />
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          onClick={handleBuscar}
          disabled={loading}
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          {loading ? 'Buscando...' : 'Buscar'}
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>{error}</Alert>
      )}

      <Grid container spacing={3}>
        {/* Top CIDs - Horizontal BarChart */}
        <Grid item xs={12} lg={7}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              CIDs Mais Frequentes
            </Typography>
            {loading ? (
              <ChartSkeleton height={350} />
            ) : topCidsFormatted.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(350, topCidsFormatted.length * 40)}>
                <BarChart data={topCidsFormatted} layout="vertical" margin={{ left: 200 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="label_truncated"
                    tick={{ fontSize: 11 }}
                    width={190}
                  />
                  <Tooltip
                    formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
                    labelFormatter={(label: any) => {
                      const item = topCidsFormatted.find((c) => c.label_truncated === label);
                      return item?.label_full || label;
                    }}
                  />
                  <Bar dataKey="total_registros" name="Registros" fill="#1565c0" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Nenhum CID encontrado no periodo.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Por Origem - Vertical BarChart */}
        <Grid item xs={12} lg={5}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Registros por Origem
            </Typography>
            {loading ? (
              <ChartSkeleton height={350} />
            ) : porOrigem.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={porOrigem}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="tabela_origem" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
                  />
                  <Bar dataKey="total_registros" name="Registros" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Nenhum registro por origem no periodo.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Evolucao Multi-Line Chart */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Evolucao Mensal por CID
        </Typography>
        {loading ? (
          <ChartSkeleton height={400} />
        ) : evolucao.length > 0 && cidKeys.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={evolucao}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
              />
              <Legend />
              {cidKeys.map((cid, idx) => (
                <Line
                  key={cid}
                  type="monotone"
                  dataKey={cid}
                  name={cid}
                  stroke={CID_COLORS[idx % CID_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Nenhum dado de evolucao no periodo.
          </Typography>
        )}
      </Paper>

      {/* Empty state */}
      {isEmpty && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum dado disponivel para CID/Diagnosticos no periodo selecionado.
          </Typography>
        </Box>
      )}

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="cid"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoCid;
